import { useMemo } from 'react';
import { useUnreadRequests } from '@/hooks/useSupportRequests';
import { useKnowledgeBaseTree } from '@/hooks/useKnowledgeBase';
import { useUserConfig } from '@/hooks/useUserConfig';

/**
 * The counts the nav shows next to Requests and the Knowledge Base.
 *
 * Lives here rather than in the sidebar because the mobile tab bar and home
 * screen show the same numbers, and the rules behind them (which side of the
 * request queue you're on; what counts as a pending KB change) are subtle
 * enough that a second copy would drift.
 */

/**
 * Staff are shown what is waiting on Pliable; everyone else what is waiting on
 * them. Same badge, opposite side of the same queue — each person sees the
 * count they can actually do something about.
 */
export function useRequestBadgeCount(enabled: boolean) {
  const { data: requestSummary } = useUnreadRequests({ enabled });
  const { data: userConfig } = useUserConfig();
  return useMemo(() => {
    const counts = requestSummary?.status_counts;
    if (!counts) return 0;
    return userConfig?.is_internal_staff
      ? (counts.awaiting_pliable ?? 0)
      : (counts.awaiting_customer ?? 0);
  }, [requestSummary, userConfig]);
}

/**
 * Pending knowledge-base changes: brand-new articles awaiting review, plus the
 * distinct articles that have a pending update queued against them. Counting
 * target ids rather than the pending rows keeps two edits to one article from
 * reading as two items to review.
 */
export function usePendingKbCount(enabled: boolean) {
  const { data: kbTree } = useKnowledgeBaseTree({ enabled });
  return useMemo(() => {
    if (!kbTree?.records) return 0;
    const itemIds = new Set(kbTree.records.map(r => r.id));
    const pendingNewCount = kbTree.records.filter(
      r => r.item_type === 'article' && r.status === 'pending' && !r.original_item_id
    ).length;
    const updateTargetIds = new Set(
      kbTree.records
        .filter(r => r.status === 'pending' && r.original_item_id && itemIds.has(r.original_item_id))
        .map(r => r.original_item_id)
    );
    return pendingNewCount + updateTargetIds.size;
  }, [kbTree]);
}
